import React from 'react';
import { format, isValid } from 'date-fns';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PlayRecording from './PlayRecording';
import { remCalc } from 'util/style-util';
import { Message } from 'types';
import { MESSAGE_SENDER as MessageSender } from 'types/enums';

type Props = {
  studentName: string;
  userId: number | undefined;
  message: Message;
  onStartPlaying?: (audio: HTMLAudioElement) => void;
};

const getTime = (dateSent: string | undefined): string =>
  !dateSent || !isValid(new Date(dateSent)) ? '--:--' : format(new Date(dateSent), 'h:mm a');

export const MessageBubble = ({ message, studentName, onStartPlaying }: Props): JSX.Element => {
  const { file, sender, dateSent, text } = message;
  const isEducator = sender === MessageSender.EDUCATOR;
  const classes = useStyles();
  const maxWidth: '50%' | 'fit-content' = '50%';

  return file && (file.mimeType || '').startsWith('audio') ? (
    <Box
      display="flex"
      justifyContent={isEducator ? 'flex-end' : 'flex-start'}
      className={classes.eachEntryMargin}
    >
      <PlayRecording
        audioLink={file.filepath}
        name={isEducator ? 'You' : studentName}
        predefinedTheme={isEducator ? MessageSender.EDUCATOR : MessageSender.STUDENT}
        details={text}
        date={dateSent}
        onStartPlaying={onStartPlaying}
        showCurrentAndDurationTimes={false}
      />
    </Box>
  ) : (
    <Box
      display="flex"
      justifyContent={isEducator ? 'flex-end' : 'flex-start'}
      className={classes.eachEntryMargin}
    >
      <Box
        pl={2}
        pr={2}
        pt={2}
        pb={2}
        maxWidth={maxWidth}
        minWidth={remCalc(225)}
        mb={2}
        bgcolor={isEducator ? 'primary.light' : 'secondary.lighter'}
        borderRadius={'16px'}
      >
        <Box mb={1} display="flex" justifyContent={isEducator ? 'flex-end' : 'flex-start'}>
          <Typography variant="body1">{isEducator ? 'You' : studentName}</Typography>
        </Box>
        <Box mb={1}>
          <Typography variant="body2" classes={{ root: classes.bodyText }}>
            {message.text}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box order={isEducator ? 0 : 1}>
            <Typography variant="body1" classes={{ root: classes.detailsText }}>
              {getTime(dateSent)}
            </Typography>
          </Box>
          <Box order={isEducator ? 1 : 0}>
            <Typography variant="body1" classes={{ root: classes.detailsText }}>
              {/*TODO: check what to display here*/}
              {/*{text}*/}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  bodyText: {
    fontWeight: '300 !important',
    whiteSpace: 'break-spaces',
    overflowWrap: 'break-word'
  },
  detailsText: {
    fontSize: remCalc(14),
    fontWeight: '300 !important'
  },
  eachEntryMargin: {
    margin: `${remCalc(10)} 0`
  }
}));
